if (!customElements.get('nosht-contact-form')) {
  customElements.define('nosht-contact-form', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      const form = this.querySelector('form');
      if (!form) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      const quickAnswers = this.closest('.nosht-contact')?.querySelector('[data-contact-quick-answers]');
      let faqTarget = null;
      let faqOwnsTabIndex = false;
      let observedNextSection = null;
      let quickAnswersTimer = null;
      const clearQuickAnswers = () => {
        if (quickAnswers) {
          quickAnswers.hidden = true;
          quickAnswers.removeAttribute('href');
        }
        if (faqOwnsTabIndex && faqTarget?.getAttribute('tabindex') === '-1') faqTarget.removeAttribute('tabindex');
        faqTarget = null;
        faqOwnsTabIndex = false;
      };
      const updateQuickAnswers = () => {
        clearQuickAnswers();
        const contactSection = this.closest('.section--nosht-contact');
        observedNextSection = contactSection?.nextElementSibling || null;
        if (!quickAnswers || !observedNextSection?.matches('.section--faq')) return;
        const target = observedNextSection.querySelector(':scope > .container[id]');
        if (!target?.id.trim() || !target.querySelector('.faq-item summary')) return;
        faqTarget = target;
        // Native fragment navigation can now focus the FAQ and continue tabbing
        // through its questions. Preserve any tabindex supplied by the theme.
        if (!target.hasAttribute('tabindex')) {
          target.setAttribute('tabindex', '-1');
          faqOwnsTabIndex = true;
        }
        quickAnswers.setAttribute('href', `#${encodeURIComponent(target.id)}`);
        quickAnswers.hidden = false;
      };
      const refreshQuickAnswersSection = event => {
        const contactSection = this.closest('.section--nosht-contact');
        if (event.target !== contactSection && event.target !== observedNextSection && event.target !== contactSection?.nextElementSibling) return;
        if (quickAnswersTimer !== null) window.clearTimeout(quickAnswersTimer);
        quickAnswersTimer = null;
        if (event.type !== 'shopify:section:unload') {
          updateQuickAnswers();
          return;
        }
        // Shopify dispatches unload before removing the old section. Hide the
        // old link immediately, then resolve its new immediate neighbour.
        clearQuickAnswers();
        quickAnswersTimer = window.setTimeout(() => {
          quickAnswersTimer = null;
          if (!signal.aborted && this.isConnected) updateQuickAnswers();
        }, 0);
      };
      this.cleanupQuickAnswers = () => {
        if (quickAnswersTimer !== null) window.clearTimeout(quickAnswersTimer);
        quickAnswersTimer = null;
        clearQuickAnswers();
        observedNextSection = null;
      };
      updateQuickAnswers();
      document.addEventListener('DOMContentLoaded', updateQuickAnswers, { once: true, signal });
      ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach(type => {
        document.addEventListener(type, refreshQuickAnswersSection, { signal });
      });
      const submitButton = form.querySelector('[data-contact-submit]');
      const submitLabel = submitButton?.querySelector('[data-contact-submit-label]');
      const originalSubmit = submitButton && submitLabel ? {
        label: submitLabel.textContent,
        disabled: submitButton.disabled,
        busy: submitButton.getAttribute('aria-busy')
      } : null;
      let pendingSubmission = null;
      let submissionTimer = null;
      let sending = false;
      const resetSubmission = () => {
        if (submissionTimer !== null) window.clearTimeout(submissionTimer);
        submissionTimer = null;
        pendingSubmission = null;
        if (!sending || !originalSubmit) return;
        submitLabel.textContent = originalSubmit.label;
        submitButton.disabled = originalSubmit.disabled;
        if (originalSubmit.busy === null) submitButton.removeAttribute('aria-busy');
        else submitButton.setAttribute('aria-busy', originalSubmit.busy);
        sending = false;
      };
      this.resetSubmission = resetSubmission;
      window.addEventListener('pageshow', resetSubmission, { signal });
      form.addEventListener('reset', resetSubmission, { signal });
      const topicFields = [
        { topic: 'order', field: form.querySelector('[data-contact-order-field]') },
        { topic: 'partner', field: form.querySelector('[data-contact-company-field]') }
      ].map(item => ({ ...item, input: item.field?.querySelector('input') }));
      const messageHelp = form.querySelector('[data-contact-message-help]');
      const updateTopicFields = () => {
        const selectedTopic = form.querySelector('[data-contact-topic]:checked')?.dataset.contactTopic;
        // Both optional fields remain usable without JavaScript. Hide and
        // disable inactive fields only after enhancement, retaining their values.
        topicFields.forEach(({ topic, field, input }) => {
          if (!field || !input) return;
          const active = selectedTopic === topic;
          field.hidden = !active;
          input.disabled = !active;
        });
        if (messageHelp) {
          const helpKey = { order: 'helpOrder', product: 'helpProduct', partner: 'helpPartner' }[selectedTopic] || 'helpDefault';
          const helpText = messageHelp.dataset[helpKey] || messageHelp.dataset.helpDefault || '';
          if (messageHelp.textContent !== helpText) messageHelp.textContent = helpText;
        }
      };
      const key = `nosht-contact:${location.pathname}:${this.dataset.sectionId}`;
      // Shopify returns email/body on validation errors. Restore only the custom
      // fields for this tab's immediately preceding attempt; no email/body draft.
      try {
        const saved = JSON.parse(sessionStorage.getItem(key) || 'null');
        sessionStorage.removeItem(key);
        if (this.querySelector('[data-contact-errors]') && saved && Date.now() - saved.time < 600000) {
          form.querySelectorAll('[data-contact-restore]').forEach(field => {
            if (!field.value && typeof saved.fields?.[field.name] === 'string') field.value = saved.fields[field.name];
          });
          form.querySelectorAll('[data-contact-topic]').forEach(topic => { topic.checked = topic.value === saved.topic; });
        }
      } catch (_) { /* Native form remains usable when browser storage is blocked. */ }
      updateTopicFields();
      form.addEventListener('change', event => {
        if (event.target.matches('[data-contact-topic]')) updateTopicFields();
      }, { signal });
      document.addEventListener('click', event => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const link = event.target.closest?.('a');
        if (!link) return;
        const cardLink = this.closest('.nosht-contact')?.contains(link) && link.dataset.contactForm === form.id && link.hasAttribute('data-contact-select-topic');
        // FAQ rich text keeps a normal anchor for the no-JavaScript fallback.
        // Enhance only this form's adjacent FAQ, including editor replacements.
        const orderEnquiry = this.id && document.getElementById(this.id) === this && link.getAttribute('href') === `#${this.id}` && observedNextSection?.matches('.section--faq') && observedNextSection.contains(link);
        if (!cardLink && !orderEnquiry) return;
        const requestedTopic = orderEnquiry ? 'order' : link.dataset.contactSelectTopic;
        const topic = [...form.querySelectorAll('[data-contact-topic]')].find(input => input.dataset.contactTopic === requestedTopic);
        if (!topic || topic.disabled) return;
        event.preventDefault();
        topic.checked = true;
        topic.dispatchEvent(new Event('change', { bubbles: true }));
        topic.focus({ preventScroll: true });
        const target = this.querySelector('.nosht-page__heading') || form;
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView({ behavior: reducedMotion ? 'instant' : 'smooth', block: 'start' });
      }, { signal });
      form.addEventListener('submit', event => {
        // Shopify CAPTCHA and other form handlers may cancel an attempt, then
        // submit again. Only guard an attempt that has not been cancelled.
        if (event.defaultPrevented) return;
        if (pendingSubmission?.defaultPrevented) resetSubmission();
        if (pendingSubmission) {
          event.preventDefault();
          return;
        }
        pendingSubmission = event;
        const fields = Object.fromEntries([...form.querySelectorAll('[data-contact-restore]')].map(field => [field.name, field.value]));
        try {
          sessionStorage.setItem(key, JSON.stringify({ time: Date.now(), fields, topic: form.querySelector('[data-contact-topic]:checked')?.value || '' }));
        } catch (_) { /* Continue native submission. */ }
        // Wait for the entire submit event to finish, including delegated
        // CAPTCHA listeners. Keep every contact field and native form intact.
        submissionTimer = window.setTimeout(() => {
          submissionTimer = null;
          if (signal.aborted || pendingSubmission !== event) return;
          if (event.defaultPrevented) {
            resetSubmission();
            return;
          }
          if (!originalSubmit) return;
          submitLabel.textContent = submitButton.dataset.sendingLabel || 'Sending…';
          submitButton.disabled = true;
          submitButton.setAttribute('aria-busy', 'true');
          sending = true;
        }, 0);
      }, { signal });
      this.querySelector('[data-contact-errors], [data-contact-success]')?.focus();
    }
    disconnectedCallback() {
      this.cleanupQuickAnswers?.();
      this.cleanupQuickAnswers = null;
      this.resetSubmission?.();
      this.resetSubmission = null;
      this.controller?.abort();
      this.controller = null;
    }
  });
}
